import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { CreateApiKeyResponse, invalidateApiKeysCache, useCreateApiKey } from 'src/api/api-keys'
import { useGetLocalitiesMinimalData } from 'src/api/localities'
import { AppForm, FormSubmitButton, FormTextField } from 'src/components/FormCore'
import { FormSwitch } from 'src/components/FormSwitch'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { buildCreateApiKeyRequest } from './ApiAccessPage.action'
import { LocalityMultiSelect } from './LocalityMultiSelect'

interface CreateApiKeyDialogProps extends ShowDialogProps {
  customerId: EntityId
  onCreated: (secret: { key: string; secret: string }) => void
}

const CreateApiKeyDialog: React.FC<CreateApiKeyDialogProps> = ({ pop, customerId, onCreated }) => {
  const { t } = useTranslation(['api-access', 'common', 'common-api-message'])
  const { data: localities } = useGetLocalitiesMinimalData(customerId)
  const createMut = useCreateApiKey(customerId)

  const createApiKeySchema = z.object({
    name: z.string().min(3, { message: t('name-required', { ns: 'api-access' }) }),
    localityIds: z.array(z.number()),
    allLocalities: z.boolean(),
  })

  type CreateApiKeyFormValues = z.infer<typeof createApiKeySchema>

  const form = useZodForm(createApiKeySchema, {
    defaultValues: { name: '', localityIds: [], allLocalities: false },
  })

  const handleSubmit = (values: CreateApiKeyFormValues) => {
    createMut.mutate(buildCreateApiKeyRequest(values.name, values), {
      onSuccess: res => {
        const data = ((res as any).data ?? res) as CreateApiKeyResponse
        invalidateApiKeysCache.list(customerId)
        onCreated({ key: data.key, secret: data.secret })
        pop(true)
      },
      onError: (error: any) => {
        toast.error(t(error?.data || 'unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }

  return (
    <AppForm form={form} onSubmit={handleSubmit}>
      <GenericDialogLayout
        title={t('create-new', { ns: 'api-access' })}
        pop={() => pop()}
        actions={[
          <FormSubmitButton
            key="submit"
            isLoading={createMut.isLoading}
            createText={t('create', { ns: 'api-access' })}
          />,
        ]}
      >
        <FormTextField name="name" label={t('name', { ns: 'api-access' })} sx={{ mb: 1 }} />
        <FormSwitch
          name="allLocalities"
          label={t('full-access-label', { ns: 'api-access' })}
          onValueChange={checked => checked && form.setValue('localityIds', [])}
        />
        <LocalityMultiSelect
          control={form.control}
          name="localityIds"
          options={localities ?? []}
          label={t('localities', { ns: 'api-access' })}
          helperText={t('localities-helper', { ns: 'api-access' })}
          disabled={form.watch('allLocalities')}
        />
      </GenericDialogLayout>
    </AppForm>
  )
}

export default CreateApiKeyDialog
